import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  IconButton,
  Button,
  Grid,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  FitnessCenter as FitnessCenterIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  RestoreFromTrash as ResetIcon,
  Restore as RestoreIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { calorieAIService } from '../../services/calorieAI/calorieAIService';
import { storageService } from '../../services/storageService';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const DEFAULT_MACRO_PERCENTAGES = {
  protein: 20,
  carbs: 55,
  fat: 25
};

const MACRO_PRESETS = [
  {
    id: 'balanced',
    group: 'diet',
    label: '均衡飲食',
    percentages: { protein: 20, carbs: 55, fat: 25 },
    description: '最適合大多數日常使用者的基礎比例。',
    bestFor: '一般健康、長期維持、剛開始使用者'
  },
  {
    id: 'high_protein',
    group: 'diet',
    label: '高蛋白',
    percentages: { protein: 30, carbs: 40, fat: 30 },
    description: '適合增肌、提升飽足感，或控制食慾時使用。',
    bestFor: '增肌、減脂期、容易餓的人'
  },
  {
    id: 'low_carb',
    group: 'diet',
    label: '低碳飲食',
    percentages: { protein: 30, carbs: 25, fat: 45 },
    description: '降低碳水占比，改由蛋白質與脂肪提供較多熱量。',
    bestFor: '想降低精緻澱粉、偏好低碳者'
  },
  {
    id: 'keto',
    group: 'diet',
    label: '生酮飲食',
    percentages: { protein: 25, carbs: 10, fat: 65 },
    description: '偏高脂、極低碳，較屬進階飲食法。',
    bestFor: '已有經驗、明確要執行生酮者'
  },
  {
    id: 'mediterranean',
    group: 'diet',
    label: '地中海',
    percentages: { protein: 20, carbs: 40, fat: 40 },
    description: '偏向好油脂與原型食物的長期型飲食模式。',
    bestFor: '長期健康、重視好油脂與原型食物'
  },
  {
    id: 'fasting_16_8',
    group: 'diet',
    label: '16:8 斷食',
    percentages: { protein: 25, carbs: 40, fat: 35 },
    description: '16:8 主要是進食時間安排；這裡提供較容易飽足的建議比例。',
    bestFor: '有時間控管習慣、想透過進食窗口管理熱量'
  },
  {
    id: 'general_health',
    group: 'goal',
    label: '一般健康',
    percentages: { protein: 20, carbs: 50, fat: 30 },
    description: '以均衡與可長期維持為優先，適合多數日常情境。',
    bestFor: '一般上班族、家庭日常、長期維持'
  },
  {
    id: 'fat_loss',
    group: 'goal',
    label: '減脂',
    percentages: { protein: 30, carbs: 35, fat: 35 },
    description: '提高蛋白質以維持飽足感與肌肉量，碳水適度下降。',
    bestFor: '減脂期、控制熱量、減少嘴饞'
  },
  {
    id: 'muscle_gain',
    group: 'goal',
    label: '增肌',
    percentages: { protein: 30, carbs: 45, fat: 25 },
    description: '保留較高蛋白並給足碳水，支援訓練表現與恢復。',
    bestFor: '重量訓練、增肌期、運動量較高'
  },
  {
    id: 'blood_sugar',
    group: 'goal',
    label: '控糖',
    percentages: { protein: 25, carbs: 30, fat: 45 },
    description: '降低碳水占比，透過蛋白質與脂肪增加穩定度與飽足感。',
    bestFor: '想減少血糖波動、少吃精緻澱粉'
  },
  {
    id: 'satiety',
    group: 'goal',
    label: '高飽足',
    percentages: { protein: 30, carbs: 30, fat: 40 },
    description: '以較高蛋白與較高脂肪換取更久的飽足感。',
    bestFor: '容易餓、常吃零食、需要延長飽足感'
  }
];

const MIN_MACRO_PERCENT = 5;

const normalizeMacroPercentages = (percentages) => {
  const safePercentages = {
    protein: Number(percentages.protein) || 0,
    carbs: Number(percentages.carbs) || 0,
    fat: Number(percentages.fat) || 0
  };

  const total = safePercentages.protein + safePercentages.carbs + safePercentages.fat;
  if (total <= 0) return { ...DEFAULT_MACRO_PERCENTAGES };

  const normalized = {
    protein: Math.round((safePercentages.protein / total) * 100),
    carbs: Math.round((safePercentages.carbs / total) * 100),
    fat: Math.round((safePercentages.fat / total) * 100)
  };

  const normalizedTotal = normalized.protein + normalized.carbs + normalized.fat;
  if (normalizedTotal !== 100) {
    const diff = 100 - normalizedTotal;
    const largestKey = Object.keys(normalized).reduce((largest, key) =>
      normalized[key] > normalized[largest] ? key : largest
    , 'carbs');
    normalized[largestKey] += diff;
  }

  return normalized;
};

const arePercentagesEqual = (left, right) =>
  left.protein === right.protein &&
  left.carbs === right.carbs &&
  left.fat === right.fat;

const findMatchingPresetId = (percentages) => {
  const normalized = normalizeMacroPercentages(percentages);
  const matchedPreset = MACRO_PRESETS.find((preset) =>
    arePercentagesEqual(normalized, normalizeMacroPercentages(preset.percentages))
  );
  return matchedPreset?.id || 'custom';
};

const getPresetById = (presetId) => MACRO_PRESETS.find((preset) => preset.id === presetId);

const GoalSetting = ({ onBack, onGoalsUpdated }) => {
  const [goals, setGoals] = useState({
    daily_calories: 2000,
    daily_protein: 150,
    daily_carbs: 250,
    daily_fat: 67,
    weight_goal: 'maintain',
    activity_level: 'moderate',
    target_weight: 70,
    current_weight: 70,
    height: 170,
    age: 25,
    gender: 'male',
    tdee: 0  // 添加 TDEE 欄位
  });

  // 營養素百分比狀態
  const [macroPercentages, setMacroPercentages] = useState(DEFAULT_MACRO_PERCENTAGES);
  const [selectedMacroPreset, setSelectedMacroPreset] = useState('balanced');

  const [showResetDialog, setShowResetDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const activityLevels = [
    { value: 'sedentary', label: '久坐 (很少運動)', multiplier: 1.2 },
    { value: 'light', label: '輕度活動 (每週1-3次)', multiplier: 1.375 },
    { value: 'moderate', label: '中度活動 (每週3-5次)', multiplier: 1.55 },
    { value: 'active', label: '高度活動 (每週6-7次)', multiplier: 1.725 },
    { value: 'very_active', label: '極高活動 (每天2次)', multiplier: 1.9 }
  ];

  const weightGoals = [
    { value: 'lose', label: '減重 🔽', calorieAdjust: -500 },
    { value: 'maintain', label: '維持 ⚖️', calorieAdjust: 0 },
    { value: 'gain', label: '增重 🔼', calorieAdjust: 500 }
  ];

  useEffect(() => {
    console.log('🔄 GoalSetting useEffect: 組件載入，開始載入目標設定');
    loadCurrentGoals();
  }, []);

  useEffect(() => {
    console.log('🔄 GoalSetting useEffect: 基本資料改變，重新計算建議卡路里', {
      current_weight: goals.current_weight,
      height: goals.height,
      age: goals.age,
      gender: goals.gender,
      activity_level: goals.activity_level,
      weight_goal: goals.weight_goal
    });
    // 當基本資料改變時自動計算建議卡路里
    calculateRecommendedCalories();
  }, [goals.current_weight, goals.height, goals.age, goals.gender, goals.activity_level, goals.weight_goal]);

  // 當百分比改變時，自動計算對應的克數
  useEffect(() => {
    const proteinGrams = Math.round((goals.daily_calories * macroPercentages.protein / 100) / 4);
    const carbsGrams = Math.round((goals.daily_calories * macroPercentages.carbs / 100) / 4);
    const fatGrams = Math.round((goals.daily_calories * macroPercentages.fat / 100) / 9);
    
    setGoals(prev => ({
      ...prev,
      daily_protein: proteinGrams,
      daily_carbs: carbsGrams,
      daily_fat: fatGrams
    }));
  }, [macroPercentages, goals.daily_calories]);

  const loadCurrentGoals = async () => {
    console.log('📥 loadCurrentGoals: 開始載入目標設定');
    try {
      setLoading(true);
      const currentGoals = await calorieAIService.getActiveGoals();
      console.log('📥 loadCurrentGoals: 從 service 獲取的數據:', currentGoals);
      if (currentGoals) {
        console.log('📥 loadCurrentGoals: 設定新的 goals 狀態');
        setGoals({ ...goals, ...currentGoals });
        console.log('📥 loadCurrentGoals: 合併後的 goals:', { ...goals, ...currentGoals });
        
        // 根據載入的克數計算百分比
        if (currentGoals.daily_calories && currentGoals.daily_protein && currentGoals.daily_carbs && currentGoals.daily_fat) {
          const proteinPercent = Math.round((currentGoals.daily_protein * 4 / currentGoals.daily_calories) * 100);
          const carbsPercent = Math.round((currentGoals.daily_carbs * 4 / currentGoals.daily_calories) * 100);
          const fatPercent = Math.round((currentGoals.daily_fat * 9 / currentGoals.daily_calories) * 100);
          
          setMacroPercentages(normalizeMacroPercentages({
            protein: proteinPercent,
            carbs: carbsPercent,
            fat: fatPercent
          }));
          setSelectedMacroPreset(findMatchingPresetId({
            protein: proteinPercent,
            carbs: carbsPercent,
            fat: fatPercent
          }));
        }
      } else {
        console.log('📥 loadCurrentGoals: 沒有獲取到數據，使用預設值');
      }
    } catch (error) {
      console.error('❌ loadCurrentGoals: 載入目標設定失敗:', error);
    } finally {
      setLoading(false);
      console.log('📥 loadCurrentGoals: 載入完成');
    }
  };

  const calculateRecommendedCalories = () => {
    const { current_weight, height, age, gender, activity_level, weight_goal } = goals;
    
    if (!current_weight || !height || !age) return;

    // 使用 Mifflin-St Jeor 公式計算基礎代謝率 (BMR)
    let bmr;
    if (gender === 'male') {
      bmr = 10 * current_weight + 6.25 * height - 5 * age + 5;
    } else {
      bmr = 10 * current_weight + 6.25 * height - 5 * age - 161;
    }

    // 根據活動水平調整
    const activityMultiplier = activityLevels.find(level => level.value === activity_level)?.multiplier || 1.55;
    let tdee = bmr * activityMultiplier;

    // 根據體重目標調整
    const weightGoalAdjust = weightGoals.find(goal => goal.value === weight_goal)?.calorieAdjust || 0;
    const recommendedCalories = Math.round(tdee + weightGoalAdjust);



    // 計算建議的巨量營養素
    const recommendedProtein = Math.round(current_weight * 2.2); // 每公斤體重2.2g蛋白質
    const recommendedFat = Math.round((recommendedCalories * 0.25) / 9); // 25%來自脂肪
    const recommendedCarbs = Math.round((recommendedCalories - (recommendedProtein * 4) - (recommendedFat * 9)) / 4);

    setGoals(prev => ({
      ...prev,
      daily_calories: recommendedCalories,
      daily_protein: recommendedProtein,
      daily_carbs: Math.max(recommendedCarbs, 100), // 最少100g碳水
      daily_fat: recommendedFat,
      tdee: Math.round(tdee)  // 保存 TDEE 值
    }));
  };

  const handleSaveGoals = async () => {
    console.log('💾 handleSaveGoals: 開始保存目標設定');
    console.log('💾 handleSaveGoals: 要保存的數據:', goals);
    try {
      setLoading(true);
      await calorieAIService.updateCalorieGoals(goals);
      console.log('✅ handleSaveGoals: 保存成功');
      onGoalsUpdated?.();
    } catch (error) {
      console.error('❌ handleSaveGoals: 保存目標設定失敗:', error);
    } finally {
      setLoading(false);
      console.log('💾 handleSaveGoals: 保存流程完成');
    }
  };

  const handleResetToDefaults = () => {
    setGoals({
      daily_calories: 2000,
      daily_protein: 150,
      daily_carbs: 250,
      daily_fat: 67,
      weight_goal: 'maintain',
      activity_level: 'moderate',
      target_weight: goals.current_weight,
      current_weight: goals.current_weight,
      height: goals.height,
      age: goals.age,
      gender: goals.gender
    });
    setMacroPercentages(DEFAULT_MACRO_PERCENTAGES);
    setSelectedMacroPreset('balanced');
    setShowResetDialog(false);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      showSnackbar('正在準備匯出檔案...', 'info');
      
      const exportedData = await calorieAIService.exportAllData();
      const parsedData = JSON.parse(exportedData);
      
      // 添加 VisionBoard 資料
      try {
        const visionData = await storageService.getItem('visionBoards.json');
        if (visionData) {
          const visionBoards = JSON.parse(visionData);
          
          // 如果是 Native 平台，我們需要讀取實際圖片轉換為 Base64 以便完整備份
          if (Capacitor.isNativePlatform()) {
             const boardsWithBase64 = await Promise.all(visionBoards.map(async (board) => {
               if (board.isDefault || !board.image) return board;
               try {
                 if (!board.image.startsWith('data:image')) {
                    const file = await Filesystem.readFile({
                      path: board.image,
                      directory: Directory.Data
                    });
                    return { ...board, image: `data:image/jpeg;base64,${file.data}` };
                 }
                 return board;
               } catch (e) {
                 console.error('Failed to read image for export', e);
                 return board;
               }
             }));
             parsedData.visionBoards = boardsWithBase64;
          } else {
             // Web 端圖片已經是 base64 了
             parsedData.visionBoards = visionBoards;
          }
        }
      } catch (err) {
        console.error('Error adding visionboards to export', err);
      }

      const finalExportString = JSON.stringify(parsedData, null, 2);
      const fileName = `supercloud_backup_${new Date().toISOString().split('T')[0]}.json`;

      if (Capacitor.isNativePlatform()) {
        // Android 平台寫入文件並分享
        const writeResult = await Filesystem.writeFile({
          path: fileName,
          data: finalExportString,
          directory: Directory.Cache,
          encoding: Encoding.UTF8
        });

        await Share.share({
          title: 'SuperCloud 備份資料',
          text: '這是您的 SuperCloud 應用程式備份資料',
          url: writeResult.uri,
          dialogTitle: '儲存備份資料'
        });
        showSnackbar('匯出成功！請選擇儲存位置', 'success');
      } else {
        // Web 平台下載文件
        const blob = new Blob([finalExportString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showSnackbar('備份檔案下載完成', 'success');
      }
    } catch (error) {
      console.error('匯出資料失敗:', error);
      showSnackbar('匯出資料失敗，請稍後重試', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      showSnackbar('正在匯入資料...', 'info');

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const content = e.target.result;
          const data = JSON.parse(content);
          
          // 匯入 CalorieAI 資料
          await calorieAIService.importData(JSON.stringify({
            foodRecords: data.foodRecords,
            goals: data.goals,
            customFoods: data.customFoods
          }));

          // 處理 VisionBoard 資料
          if (data.visionBoards) {
            let processedBoards = data.visionBoards;
            
            // 如果是 Native 平台，需要把 Base64 寫回文件系統
            if (Capacitor.isNativePlatform()) {
               processedBoards = await Promise.all(data.visionBoards.map(async (board) => {
                 if (board.isDefault || !board.image || !board.image.startsWith('data:image')) return board;
                 try {
                    const filename = `vision_${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`;
                    const base64Data = board.image.split(',')[1];
                    
                    // 確保目錄存在
                    try {
                      await Filesystem.mkdir({ path: 'visionboards', directory: Directory.Data, recursive: true });
                    } catch(e) {}

                    await Filesystem.writeFile({
                      path: `visionboards/${filename}`,
                      data: base64Data,
                      directory: Directory.Data,
                      recursive: true
                    });
                    
                    return { ...board, image: `visionboards/${filename}` };
                 } catch (imgErr) {
                    console.error('Failed to save imported image', imgErr);
                    return board; // 保留原始數據或捨棄
                 }
               }));
            }
            
            await storageService.setItem('visionBoards.json', JSON.stringify(processedBoards));
          }

          showSnackbar('資料匯入成功！即將重新載入...', 'success');
          setTimeout(() => {
            onGoalsUpdated?.();
            window.location.reload(); // 簡單暴力的重載以確保所有狀態更新
          }, 1500);

        } catch (err) {
          console.error('解析匯入檔案失敗:', err);
          showSnackbar('檔案格式錯誤，匯入失敗', 'error');
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('讀取檔案失敗:', error);
      showSnackbar('讀取檔案失敗', 'error');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setLoading(false);
    }
  };

  const getBMI = () => {
    if (!goals.current_weight || !goals.height) return 0;
    return (goals.current_weight / Math.pow(goals.height / 100, 2)).toFixed(1);
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { text: '體重過輕', color: '#2196F3' };
    if (bmi < 24) return { text: '正常範圍', color: '#4CAF50' };
    if (bmi < 27) return { text: '體重過重', color: '#FF9800' };
    return { text: '肥胖', color: '#F44336' };
  };

  const getTDEE = () => {
    const { current_weight, height, age, gender, activity_level } = goals;
    
    if (!current_weight || !height || !age) return 0;

    // 使用 Mifflin-St Jeor 公式計算基礎代謝率 (BMR)
    let bmr;
    if (gender === 'male') {
      bmr = 10 * current_weight + 6.25 * height - 5 * age + 5;
    } else {
      bmr = 10 * current_weight + 6.25 * height - 5 * age - 161;
    }

    // 根據活動水平調整
    const activityMultiplier = activityLevels.find(level => level.value === activity_level)?.multiplier || 1.55;
    const tdee = Math.round(bmr * activityMultiplier);

    return tdee;
  };

  const renderBasicInfo = () => (
    <Card sx={{ mb: 3, borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1, color: '#FF6B6B' }} />
          基本資料
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="gender-label" shrink>性別</InputLabel>
              <Select
                labelId="gender-label"
                label="性別"
                value={goals.gender}
                onChange={(e) => setGoals({ ...goals, gender: e.target.value })}
                displayEmpty
              >
                <MenuItem value="male">男性 👨</MenuItem>
                <MenuItem value="female">女性 👩</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="年齡"
              type="number"
              value={goals.age}
              onChange={(e) => setGoals({ ...goals, age: e.target.value === '' ? '' : parseInt(e.target.value) })}
              inputProps={{ min: 10, max: 100 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="身高 (cm)"
              type="number"
              value={goals.height}
              onChange={(e) => setGoals({ ...goals, height: e.target.value === '' ? '' : parseInt(e.target.value) })}
              inputProps={{ min: 100, max: 250 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="目前體重 (kg)"
              type="number"
              value={goals.current_weight}
              onChange={(e) => setGoals({ ...goals, current_weight: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              inputProps={{ min: 30, max: 200, step: 0.1 }}
            />
          </Grid>
        </Grid>

        {goals.current_weight && goals.height && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  BMI 指數: <strong>{getBMI()}</strong>
                </Typography>
                <Chip 
                  label={getBMICategory(parseFloat(getBMI())).text}
                  sx={{ 
                    backgroundColor: getBMICategory(parseFloat(getBMI())).color,
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  TDEE: <strong>{getTDEE()} 卡</strong>
                </Typography>
                <Chip 
                  label="每日總消耗"
                  sx={{ 
                    backgroundColor: '#9C27B0',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderActivityAndGoals = () => (
    <Card sx={{ mb: 3, borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <FitnessCenterIcon sx={{ mr: 1, color: '#FF6B6B' }} />
          活動水平與目標
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="activity-label" shrink>活動水平</InputLabel>
              <Select
                labelId="activity-label"
                label="活動水平"
                value={goals.activity_level}
                onChange={(e) => setGoals({ ...goals, activity_level: e.target.value })}
                displayEmpty
              >
                {activityLevels.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="weight-goal-label" shrink>體重目標</InputLabel>
              <Select
                labelId="weight-goal-label"
                label="體重目標"
                value={goals.weight_goal}
                onChange={(e) => setGoals({ ...goals, weight_goal: e.target.value })}
                displayEmpty
              >
                {weightGoals.map((goal) => (
                  <MenuItem key={goal.value} value={goal.value}>
                    {goal.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {goals.weight_goal !== 'maintain' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="目標體重 (kg)"
                type="number"
                value={goals.target_weight}
                onChange={(e) => setGoals({ ...goals, target_weight: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                inputProps={{ min: 30, max: 200, step: 0.1 }}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const handleMacroDistributionChange = (event, newValue, activeThumb) => {
    if (!Array.isArray(newValue)) return;

    let [proteinBoundary, carbsBoundary] = newValue;

    if (activeThumb === 0) {
      proteinBoundary = Math.max(MIN_MACRO_PERCENT, Math.min(proteinBoundary, carbsBoundary - MIN_MACRO_PERCENT));
    } else {
      carbsBoundary = Math.min(100 - MIN_MACRO_PERCENT, Math.max(carbsBoundary, proteinBoundary + MIN_MACRO_PERCENT));
    }

    const nextPercentages = normalizeMacroPercentages({
      protein: proteinBoundary,
      carbs: carbsBoundary - proteinBoundary,
      fat: 100 - carbsBoundary
    });

    setMacroPercentages(nextPercentages);
    setSelectedMacroPreset(findMatchingPresetId(nextPercentages));
  };

  const handleApplyMacroPreset = (presetId) => {
    const preset = getPresetById(presetId);
    if (!preset) return;

    const normalized = normalizeMacroPercentages(preset.percentages);
    setMacroPercentages(normalized);
    setSelectedMacroPreset(presetId);
  };

  const renderNutritionGoals = () => (
    <Card sx={{ mb: 3, borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <TrendingUpIcon sx={{ mr: 1, color: '#FF6B6B' }} />
          營養目標設定
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              每日卡路里目標: {goals.daily_calories} 卡
            </Typography>
            <Slider
              value={goals.daily_calories}
              onChange={(e, value) => setGoals({ ...goals, daily_calories: value })}
              min={1200}
              max={4000}
              step={50}
              marks={[
                { value: 1200, label: '1200' },
                { value: 2000, label: '2000' },
                { value: 3000, label: '3000' },
                { value: 4000, label: '4000' }
              ]}
              sx={{
                mx: 1,
                width: '95%',
                '& .MuiSlider-thumb': { backgroundColor: '#FF6B6B' },
                '& .MuiSlider-track': { backgroundColor: '#FF6B6B' },
                '& .MuiSlider-rail': { backgroundColor: '#FFE0E0' }
              }}
            />
          </Grid>
        </Grid>

        {/* 營養比例視覺化與主控制器 */}
        <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            飲食模式建議
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            如果不確定比例怎麼配，可以先選一個常見飲食法，再用下方比例條微調。
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
            常見飲食法
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {MACRO_PRESETS.filter((preset) => preset.group === 'diet').map((preset) => (
              <Chip
                key={preset.id}
                label={preset.label}
                clickable
                color={selectedMacroPreset === preset.id ? 'primary' : 'default'}
                variant={selectedMacroPreset === preset.id ? 'filled' : 'outlined'}
                onClick={() => handleApplyMacroPreset(preset.id)}
              />
            ))}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
            目標導向建議
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {MACRO_PRESETS.filter((preset) => preset.group === 'goal').map((preset) => (
              <Chip
                key={preset.id}
                label={preset.label}
                clickable
                color={selectedMacroPreset === preset.id ? 'primary' : 'default'}
                variant={selectedMacroPreset === preset.id ? 'filled' : 'outlined'}
                onClick={() => handleApplyMacroPreset(preset.id)}
              />
            ))}
            {selectedMacroPreset === 'custom' && (
              <Chip
                label="自訂比例"
                color="secondary"
                variant="filled"
              />
            )}
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 3 }}>
            {(getPresetById(selectedMacroPreset)?.description) ||
              '您目前已手動微調比例，這組設定屬於自訂模式。'}
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 3 }}>
            適合對象：
            {' '}
            {(getPresetById(selectedMacroPreset)?.bestFor) ||
              '想自行微調比例，依自己的生活型態與偏好設定。'}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            營養比例分布: (總計 {macroPercentages.protein + macroPercentages.carbs + macroPercentages.fat}%)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            拖曳下方兩個控制點，直接調整蛋白質、碳水與脂肪的比例，系統會自動保持總計為 100%。
          </Typography>
          <Box sx={{ mx: 1, width: '95%' }}>
            <Box sx={{ display: 'flex', mb: 2, overflow: 'hidden', borderRadius: '999px' }}>
              <Box 
                sx={{ 
                  flex: macroPercentages.protein,
                  height: '24px',
                  backgroundColor: '#4CAF50'
                }}
              />
              <Box 
                sx={{ 
                  flex: macroPercentages.carbs,
                  height: '24px',
                  backgroundColor: '#2196F3'
                }}
              />
              <Box 
                sx={{ 
                  flex: macroPercentages.fat,
                  height: '24px',
                  backgroundColor: '#FF9800'
                }}
              />
            </Box>
          </Box>
          <Slider
            value={[macroPercentages.protein, macroPercentages.protein + macroPercentages.carbs]}
            onChange={handleMacroDistributionChange}
            min={0}
            max={100}
            step={1}
            disableSwap
            marks={[
              { value: 0, label: '0%' },
              { value: 25, label: '25%' },
              { value: 50, label: '50%' },
              { value: 75, label: '75%' },
              { value: 100, label: '100%' }
            ]}
            sx={{
              mx: 1,
              width: '95%',
              color: '#9e9e9e',
              '& .MuiSlider-rail': {
                opacity: 1,
                backgroundColor: '#d6d6d6'
              },
              '& .MuiSlider-track': {
                backgroundColor: 'transparent',
                border: 'none'
              },
              '& .MuiSlider-thumb': {
                width: 18,
                height: 18,
                backgroundColor: '#fff',
                border: '3px solid #9e9e9e',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
              },
              '& .MuiSlider-mark': {
                backgroundColor: '#bdbdbd',
                height: 8
              },
              '& .MuiSlider-markLabel': {
                color: '#666'
              }
            }}
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, backgroundColor: '#E8F5E8', borderRadius: '10px' }}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                  🥩 蛋白質
                </Typography>
                <Typography variant="body2">
                  {macroPercentages.protein}% ({goals.daily_protein}g)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  建議範圍: 15-25%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, backgroundColor: '#E3F2FD', borderRadius: '10px' }}>
                <Typography variant="subtitle2" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
                  🍞 碳水化合物
                </Typography>
                <Typography variant="body2">
                  {macroPercentages.carbs}% ({goals.daily_carbs}g)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  建議範圍: 50-60%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, backgroundColor: '#FFF3E0', borderRadius: '10px' }}>
                <Typography variant="subtitle2" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
                  🥑 脂肪
                </Typography>
                <Typography variant="body2">
                  {macroPercentages.fat}% ({goals.daily_fat}g)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  建議範圍: 25-30%
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'white' }}>
      {/* 頂部導航 */}
      <Paper sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'white',
        borderRadius: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            目標設定
          </Typography>
        </Box>
        <IconButton onClick={() => setShowResetDialog(true)} sx={{ color: '#666' }}>
          <ResetIcon />
        </IconButton>
      </Paper>



      {/* 內容區域 */}
      <Box sx={{ p: 2, pb: 10 }}>
        {renderBasicInfo()}
        {renderActivityAndGoals()}
        {renderNutritionGoals()}

        {/* 底部操作按鈕區 */}
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 保存按鈕 */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSaveGoals}
            disabled={loading}
            sx={{
              borderRadius: '12px',
              py: 2,
              background: 'linear-gradient(135deg, #4CAF50, #66BB6A)',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {loading ? '處理中...' : '保存設定'}
          </Button>

          <Divider sx={{ my: 1 }}>資料管理</Divider>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportData}
                disabled={loading}
                sx={{ borderRadius: '12px', py: 1.5 }}
              >
                匯出資料
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleImportClick}
                disabled={loading}
                sx={{ borderRadius: '12px', py: 1.5 }}
              >
                匯入資料
              </Button>
              <input
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* 重置確認對話框 */}
      <Dialog 
        open={showResetDialog} 
        onClose={() => setShowResetDialog(false)}
      >
        <DialogTitle>重置設定</DialogTitle>
        <DialogContent>
          <Typography>
            確定要重置所有目標設定為預設值嗎？此操作無法復原。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetDialog(false)}>取消</Button>
          <Button 
            onClick={handleResetToDefaults}
            color="error"
            variant="contained"
          >
            確認重置
          </Button>
        </DialogActions>
      </Dialog>

      {/* 提示訊息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GoalSetting;
